import { toaster } from '@/components/ui/toast'
import { api } from '@api'
import type { Message } from '@hvf/contracts'
import { useChannelsReads } from '@state/channelReads/state'
import { useChannels } from '@state/channels/channels'
import { userId } from '@stateApp'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { createInfiniteQuery, createQuery } from '@tanstack/solid-query'
import { createEffect, createMemo, createSignal, on, onCleanup, untrack } from 'solid-js'
import { useService } from 'solid-services'
import { getChannel } from './resources/getChannel'
import type { DisplayMessage } from './resources/getChannelHistory'
import { getChannelHistoryAtId } from './resources/getChannelHistoryAtId'
import { getPaginatedChannelHistory } from './resources/getPaginatedChannelHistory'

export type ChatMode = 'latest' | 'history'

export function useChannel() {
  const [scrollTo, setScrollTo] = createSignal<{ id: string; isHighlight?: boolean }>()
  const [chatMode, setChatMode] = createSignal<ChatMode>('latest')
  const [focusMessageId, setFocusMessageId] = createSignal<string>()
  const [activeChannelId, setActiveChannelId] = createSignal<string | undefined>()
  const [notAtBottom, setNotAtBottom] = createSignal<boolean>()
  const [missedMessagesCount, setMissedMessagesCount] = createSignal(0)

  const { setChannelRead, channelReadDictionary } = useService(useChannelsReads)()
  const { mutateChannels } = useService(useChannels)()

  // Real-time store for incoming mutations
  const [realtimeMessages, setRealtimeMessages] = createSignal<DisplayMessage[]>([])
  let subscription: RealtimeChannel | undefined

  createEffect(() => {
    if (chatMode() === 'latest') {
      setMissedMessagesCount(0)
    }
  })

  // Let the Router control chatMode/focusId signals so we don't create race conditions on deep links.
  createEffect(
    on(activeChannelId, (currentActiveId, prevActiveId) => {
      // Only run this if the channel ACTUALLY changed
      if (currentActiveId && currentActiveId !== prevActiveId) {
        setRealtimeMessages([])
        setMissedMessagesCount(0)

        mutateChannels((prevChannels) => {
          if (!prevChannels) return prevChannels
          const channel = prevChannels.find(c => c.id === currentActiveId)
          if (channel && channel.unread_count === 0) {
            return prevChannels
          }
          return prevChannels.map((channel) =>
            channel.id === currentActiveId ? { ...channel, unread_count: 0 } : channel
          )
        })
      }
    })
  )

  createEffect(
    on(
      () => {
        const t = timeline()
        return t.length > 0 ? t[t.length - 1]?.id : undefined
      },
      (lastMessageId) => {
        if (!lastMessageId) return
        const currentTimeline = untrack(() => timeline())
        const lastMessage = currentTimeline[currentTimeline.length - 1]
        const z = untrack(() => channelReadDictionary())

        setChannelRead({
          channel_id: lastMessage.channel_id,
          last_read_message_id: lastMessage.id,
          is_subscribed: z[lastMessage.channel_id]?.is_subscribed ?? false,
        })
      }
    )
  )

  const channelQuery = createQuery(() => ({
    queryKey: ['channel', activeChannelId()],
    queryFn: () => getChannel(activeChannelId()!),
    enabled: !!activeChannelId(),
    meta: {
      onError: (error: any) => {
        toaster.create({
          title: 'Failed to load channel',
          description: 'Could not fetch channel meta properties.',
          type: 'error',
        })
      }
    }
  }))

  const historyQuery = createInfiniteQuery(() => {
    const channelId = activeChannelId()
    const focusId = focusMessageId()

    return {
      queryKey: ['channelHistory', channelId, focusId || 'latest'],
      enabled: !!channelId,
      initialPageParam: null as string | null,

      queryFn: async ({ pageParam, direction }) => {
        if (!channelId) return []

        if (!pageParam && focusId) {
          const data = await getChannelHistoryAtId(channelId, focusId)
          setChatMode('history')
          return data || []
        }

        const fetchDirection = direction === 'backward' ? 'newer' : 'older'
        return getPaginatedChannelHistory(channelId, pageParam as string | null, fetchDirection)
      },

      getNextPageParam: (lastPage) => {
        if (!lastPage || lastPage.length === 0) return null
        return lastPage[0].created_at
      },

      getPreviousPageParam: (firstPage) => {
        if (!firstPage || firstPage.length === 0) return null
        return firstPage[firstPage.length - 1].created_at
      },
      meta: {
        onError: (error: any) => {
          toaster.create({
            title: 'Chat History Error',
            description: 'Failed to retrieve messages for this channel.',
            type: 'error',
          })
        }
      }
    }
  })

  createEffect(
    on(activeChannelId, (id) => {
      if (!id) return

      const apiClient = untrack(() => api())
      const subscription = apiClient
        .channel(`custom-insert-channel-${id}`)
        .on<Message>(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'message', filter: `channel_id=eq.${id}` },
          (payload) => {
            if (payload.eventType === 'INSERT') {
              const newMessage = payload.new as DisplayMessage
              const isMyMessage = newMessage.created_by === userId()

              // If I send a message while in history, instantly snap back to latest.
              if (chatMode() === 'history' && isMyMessage) {
                setChatMode('latest')
                setFocusMessageId(undefined)
              }

              const currentChatMode = chatMode()
              const hasPrevPage = historyQuery.hasPreviousPage

              // If hasPrevPage is false, we are caught up to the present. Allow inserts.
              const hasUnloadedNewerMessages = currentChatMode === 'history' && hasPrevPage !== false

              if (hasUnloadedNewerMessages) {
                // Increment missed messages counter when we block an incoming message in history mode
                setMissedMessagesCount((prev) => prev + 1)
                return
              }

              setRealtimeMessages(prev => [...prev, newMessage])

              const isScrolledUp = notAtBottom()

              // Always scroll down for our own messages, or if we're already at the bottom
              if (isMyMessage || !isScrolledUp) {
                setScrollTo({ id: newMessage.id, behavior: 'smooth', align: 'end' })
              }
            }

            if (payload.eventType === 'UPDATE') {
              const updatedMessage = payload.new as DisplayMessage
              setRealtimeMessages((prev) => {
                const exists = prev.some((m) => m.id === updatedMessage.id)
                if (exists) {
                  return prev.map((m) =>
                    m.id === updatedMessage.id ? { ...m, ...updatedMessage } : m
                  )
                }
                return [...prev, updatedMessage]
              })
            }
          }
        )
        .subscribe()

      onCleanup(() => {
        subscription?.unsubscribe()
      })
    })
  )

  const timeline = createMemo(() => {
    const historyPages = historyQuery.data?.pages.flat() || []
    const currentRealtime = realtimeMessages()

    const combined = [...historyPages, ...currentRealtime]
    const uniqueMap = new Map<string, DisplayMessage>()
    combined.forEach(msg => uniqueMap.set(msg.id, msg))

    const sorted = Array.from(uniqueMap.values()).sort((a, b) => {
      if (a.created_at !== b.created_at) {
        return a.created_at > b.created_at ? 1 : -1
      }
      return a.id > b.id ? 1 : -1
    })

    return sorted.map((message, index, arr) => {
      const prevMessage = arr[index - 1]
      const nextMessage = arr[index + 1]

      return {
        ...message,
        _isFirstInGroup: prevMessage?.created_by !== message.created_by,
        _isLastInGroup: nextMessage?.created_by !== message.created_by
      }
    })
  })

  const isReadOnly = () => channelQuery.data?.is_read_only

  return {
    activeChannelId,
    setActiveChannelId,
    channelQuery,
    notAtBottom,
    setNotAtBottom,
    historyQuery,
    timeline,
    scrollTo,
    setScrollTo,
    setChatMode,
    chatMode,
    focusMessageId,
    setFocusMessageId,
    isReadOnly,
    setRealtimeMessages,
    missedMessagesCount
  }
}
